import React, { useEffect, useMemo, useRef, useState } from "react";
import { motion, useInView, AnimatePresence, animate } from "motion/react";
import NumberFlow from "@number-flow/react";
import { AreaChart, Area, XAxis, ResponsiveContainer, Tooltip } from "recharts";
import {
    DollarSign, CalendarDays, Megaphone, FilePenLine, AlertTriangle,
    Globe, ChevronDown, FileText, Clock, TrendingUp, Lock, Wifi,
    RotateCcw, Bell, CalendarCheck2, CalendarX,
    Users, Eye, Timer, ArrowUpRight, ArrowDownRight,
    Disc, Minus, Music, RotateCw, Percent, ShieldCheck,
} from "lucide-react";

// ─────────────────────────────────────────────────────────────────────────────
// TYPES
// ─────────────────────────────────────────────────────────────────────────────

interface ContractVariable { id: string; name: string; value?: string | null; type?: string }

// ─────────────────────────────────────────────────────────────────────────────
// Path A engine-passthrough types — minimal frontend mirrors of the canonical
// db-model types (DealFactsRaw, DealScore, EqCardSet). The api-server adapter
// passes the engine output through verbatim on `/getContract` so this card
// grid can render real per-section data instead of falling back to DEMO. Keep
// field shapes loose (Partial-ish, all optional) since the engine occasionally
// emits null/undefined for fields it couldn't extract.
// ─────────────────────────────────────────────────────────────────────────────

interface PartyShape { name?: string; role?: string; address?: string }
interface MonetaryAmountShape { amountMajor?: number | null; currency?: string | null }
interface FlexibleDurationShape {
    kind?: 'fixed' | 'indefinite' | 'renewable' | 'other' | string;
    years?: number | null;
    description?: string | null;
}
interface MasterOwnershipShape { kind?: string; description?: string }

interface EngineDealFactsRaw {
    dealCurrency?: string | null;
    parties?: PartyShape[];
    effectiveDate?: string | null;
    totalAdvance?: MonetaryAmountShape | null;
    backendRoyaltyPercent?: number | null;
    masterOwnership?: MasterOwnershipShape | null;
    term?: FlexibleDurationShape | null;
    deliverables?: string[];
}

interface DealScoreFactorEntry {
    score?: number | null;
    available?: boolean;
    reason?: string;
}
interface EngineDealScore {
    overall?: number;
    label?: 'Strong' | 'Fair' | 'Weak' | 'Predatory' | string;
    breakdown?: {
        advance?: DealScoreFactorEntry;
        royalty?: DealScoreFactorEntry;
        ownership?: DealScoreFactorEntry;
        term?: DealScoreFactorEntry;
        postTermControl?: DealScoreFactorEntry;
        curePeriod?: DealScoreFactorEntry;
    };
}

interface EngineCardFact { label?: string; value?: string }
interface EngineEqCardBase {
    title?: string;
    summary?: string;
    facts?: EngineCardFact[];
}
interface EngineEqMoneyCard extends EngineEqCardBase {
    primaryAmount?: number | null;
    primaryCurrency?: string | null;
}
interface EngineEqOwnershipCard extends EngineEqCardBase {
    segments?: { name?: string; valuePercent?: number }[];
}
interface EngineEqResponsibilitiesCard extends EngineEqCardBase {
    items?: string[];
}
interface EngineEqCardSet {
    cards?: {
        money?: EngineEqMoneyCard;
        ownership?: EngineEqOwnershipCard;
        responsibilities?: EngineEqResponsibilitiesCard;
        duration?: EngineEqCardBase;
        legal?: EngineEqCardBase;
    };
}

interface ContractData {
    contractExtraction?: {
        variables?: Record<string, ContractVariable>;
        parties?: string[];
    };
    eqData?: {
        cards?: {
            moneyYouReceive?: { cardUniqueData?: { MONEY_RECEIVED?: { majorNumber?: string; paidAfterList?: string[] } } };
            howLongThisDealLasts?: { cardUniqueData?: { DURATION?: { durationText?: string } } };
            whatYoureResponsibleFor?: { cardUniqueData?: { RESPONSIBILITIES?: { responsibilities?: { title?: string; description?: string }[] } } };
        };
    };
    // Path A engine passthroughs — see api-server contracts.ts:adaptEngineOutputToContractResponse
    dealFacts?: EngineDealFactsRaw;
    dealScore?: EngineDealScore;
    eqCardsRaw?: EngineEqCardSet;
}

// ─────────────────────────────────────────────────────────────────────────────
// RECORDING SERVICES AND MASTER RIGHTS AGREEMENT
// Midnight Records LLC  ↔  Alexandra Rivers  |  Effective Jan 15, 2024
// All values parsed directly from the contract text supplied by the user.
// ─────────────────────────────────────────────────────────────────────────────

const DEMO = {
    identity: {
        type: "Recording Services & Master Rights Agreement",
        label: "Midnight Records LLC",
        artist: "Alexandra Rivers",
        governingLaw: "State of California (LA County)",
        effectiveDate: "January 15, 2024",
        status: "Under Review",
        version: "Draft v1",
    },

    economics: {
        totalAdvance: 420_000,
        // Advance instalments — §3.2
        advances: [
            { label: "On Execution",               amount: 145_000, isRecoup: false },
            { label: "First EP Delivery",           amount:  80_000, isRecoup: false },
            { label: "Second EP Commencement",      amount: 100_000, isRecoup: false },
            { label: "Production Milestones (est)", amount:  95_000, isRecoup: false },
        ],
        // Recoupable cost — §4.1-4.2
        recoupAmount: 95_000,
        // Backend royalty — §5.1 (only after full recoupment)
        backendRoyaltyPct: 3,
        // Cumulative cash-flow timeline for chart
        areaData: [
            { month: "Jan '24", value: 145_000 },
            { month: "Apr '24", value: 225_000 },
            { month: "Jul '24", value: 325_000 },
            { month: "Oct '24", value: 420_000 },
        ],
    },

    term: {
        // §6.1-6.3
        initialTerm: "2 years",
        options: "2 × 1-year at Label's option",
        postTermControl: "3 years post-term",
        noticePeriod: "Written notice before term end",
        curePeriod: "30 days for material breach",
        keyDates: [
            { label: "Effective Date",    date: "Jan 15, 2024", note: "Agreement takes effect",                 icon: "CalendarCheck2" },
            { label: "First EP Due",      date: "~Jul 2024",    note: "First EP delivery (est. 6 months)",     icon: "Disc"           },
            { label: "Option Window",     date: "Jan 2025",     note: "Label's 1st option period opens",       icon: "Bell"           },
            { label: "Term Expiry",       date: "Jan 14, 2026", note: "End of Initial Term",                   icon: "CalendarX"      },
        ],
    },

    rights: {
        territory: "Worldwide",
        exclusivity: "Exclusive recording services during term (§2.3)",
        masterOwnership: "Works-made-for-hire → Label owns in perpetuity (§7.1-7.2)",
        postTermControl: "Label retains exploitation rights for 3 years post-term (§6.3)",
        artistRetains: "Name, likeness, pre-existing IP (§7.3)",
        channels: ["Streaming", "Physical", "Digital Download", "Sync Licensing"],
    },

    obligations: [
        { deliverable: "12 commercially satisfactory Masters",  due: "During Initial Term",     note: "Per Label's quality standards — §2.2",   icon: "Music"  },
        { deliverable: "Exclusive recording services",           due: "Ongoing during term",     note: "No third-party recording without consent — §2.3", icon: "Lock"        },
        { deliverable: "Stems & session file delivery",          due: "Per album milestone",     note: "Required for master ownership — §1.2",    icon: "Disc"        },
        { deliverable: "Promotional cooperation",                due: "As scheduled by Label",   note: "Interviews, press, appearances — §8.2",   icon: "Users"       },
        { deliverable: "Timely delivery of Masters",             due: "Per production schedule", note: "Failure may suspend advances — §2.4",     icon: "Clock"       },
    ],

    clauses: [
        { title: "Advance & Payment Schedule (§3)",     summary: "Total Advance of $420,000 paid in instalments: $145K on execution, $80K on first EP, $100K on second EP commencement, balance per written production milestones. All payments subject to tax withholding.", flag: null },
        { title: "Recoupment (§4)",                     summary: "Recording costs (~$95,000) are recoupable from royalties owed to Artist. No backend royalty is payable until all recoupable amounts—including Total Advance balance—are fully recovered by Label.", flag: "No royalties until full recoupment of both the $95K costs AND the entire $420K advance balance." },
        { title: "Backend Royalty (§5)",                summary: "After full recoupment, Artist receives 3% of Gross Receipts from Masters exploitation. Payments within 45 days of each accounting period. Label may hold commercially reasonable reserves.", flag: "3% is below industry standard (typically 15–25% for recording royalties at this advance level)." },
        { title: "Master Ownership (§7.1-7.2)",         summary: "All Masters created under this Agreement are works made for hire owned by Label in perpetuity. To the extent any rights don't qualify as WMFH, Artist assigns all rights irrevocably to Label.", flag: "Perpetual Label ownership — no reversion rights negotiated." },
        { title: "Post-Term Control (§6.3)",            summary: "Following expiration or termination, Label retains Post-Term Control over delivered Masters for 3 years for continued commercial exploitation. Rights revert to Artist after that period, subject to third-party obligations.", flag: null },
        { title: "Termination & Survival (§10)",        summary: "Either party may terminate for material breach uncured within 30 days of written notice. On termination, Label retains ownership and exploitation rights in all accepted Masters. Payment obligations, indemnities, and confidentiality survive termination.", flag: null },
        { title: "Options (§6.2)",                      summary: "Label has up to 2 separate one-year option periods exercisable by written notice before expiration of the current term. Artist has no corresponding right to trigger or decline options.", flag: "Unilateral option right — Artist cannot reject renewal." },
        { title: "Label Release Control (§8.3)",        summary: "Label has approval rights over release strategy, artwork, and final metadata formatting. Label is obligated to use commercially reasonable efforts to market and distribute Masters.", flag: null },
    ],

    dealScore: {
        overall: 62,
        label: "Fair Deal",
        sublabel: "Several artist-unfavorable terms offset a strong advance",
        factors: [
            { name: "Advance Size",         score: 88, weight: 25, note: "$420K is strong for this stage",                      positive: true  },
            { name: "Royalty Rate",          score: 28, weight: 20, note: "3% vs 15–25% industry standard",                      positive: false },
            { name: "Master Ownership",      score: 15, weight: 20, note: "Perpetual WMFH — no reversion rights",                positive: false },
            { name: "Term & Flexibility",    score: 55, weight: 15, note: "2-yr initial + 2 unilateral options",                  positive: false },
            { name: "Post-Term Control",     score: 60, weight: 10, note: "3 yrs post-term (down from 5)",                       positive: true  },
            { name: "Cure Period",           score: 75, weight: 10, note: "30 days — fair and above minimum",                    positive: true  },
        ],
    },

    redlineHistory: [
        { clause: "Total Advance",           from: "$380,000",        to: "$420,000",             delta: "+$40K",      positive: true,  timestamp: "Jan 14 · 4:12 PM",  author: "Artist Manager", initials: "AM" },
        { clause: "Backend Royalty Rate",    from: "2%",              to: "3%",                   delta: "+1%",        positive: true,  timestamp: "Jan 13 · 11:30 AM", author: "Artist Counsel", initials: "AC" },
        { clause: "Signing Bonus",           from: "$120,000",        to: "$145,000",             delta: "+$25K",      positive: true,  timestamp: "Jan 12 · 9:15 AM",  author: "Artist Manager", initials: "AM" },
        { clause: "Recording Costs Cap",     from: "Uncapped",        to: "~$95,000",             delta: "Capped",     positive: true,  timestamp: "Jan 10 · 3:44 PM",  author: "Artist Counsel", initials: "AC" },
        { clause: "Post-Term Control",       from: "5 years",         to: "3 years",              delta: "-2 years",   positive: true,  timestamp: "Jan 8 · 2:07 PM",   author: "Artist Counsel", initials: "AC" },
        { clause: "Reversion Rights",        from: "After 7 years",   to: "Not included",         delta: "Removed",    positive: false, timestamp: "Jan 6 · 10:20 AM",  author: "Label Counsel",  initials: "LC" },
        { clause: "Cure Period",             from: "14 days",         to: "30 days",              delta: "+16 days",   positive: true,  timestamp: "Jan 4 · 4:55 PM",   author: "Artist Counsel", initials: "AC" },
        { clause: "Option Periods",          from: "3 × 1-year",      to: "2 × 1-year",           delta: "-1 option",  positive: true,  timestamp: "Dec 28 · 1:33 PM",  author: "Artist Manager", initials: "AM" },
    ],
};

// ─────────────────────────────────────────────────────────────────────────────
// LIVE VARIABLE PARSER
// ─────────────────────────────────────────────────────────────────────────────

function parseTaggedVars(text: string): Record<string, string> {
    const result: Record<string, string> = {};
    const regex = /<variable[^>]+id="([^"]+)"[^>]*>([^<]*)<\/variable>/g;
    let m: RegExpExecArray | null;
    while ((m = regex.exec(text)) !== null) {
        if (!result[m[1]]) result[m[1]] = m[2].trim();
    }
    return result;
}

function parseDollars(str?: string | null): number {
    if (!str) return 0;
    const cleaned = str.replace(/[$,\s]/g, "");
    const m = cleaned.match(/^(-?[\d.]+)\s*([KkMm]?)$/);
    if (!m) return 0;
    const n = parseFloat(m[1]);
    if (isNaN(n)) return 0;
    const suffix = m[2].toUpperCase();
    if (suffix === "K") return n * 1_000;
    if (suffix === "M") return n * 1_000_000;
    return n;
}

// ─────────────────────────────────────────────────────────────────────────────
// ANIMATION
// ─────────────────────────────────────────────────────────────────────────────

const stagger = { hidden: {}, show: { transition: { staggerChildren: 0.07 } } };
const fadeUp = {
    hidden: { opacity: 0, y: 14 },
    show: { opacity: 1, y: 0, transition: { type: "spring" as const, stiffness: 280, damping: 26 } },
};

// ─────────────────────────────────────────────────────────────────────────────
// ICON MAP
// ─────────────────────────────────────────────────────────────────────────────

const ICON_MAP: Record<string, React.ReactNode> = {
    CalendarCheck2: <CalendarCheck2 className="h-4 w-4" />,
    Disc:           <Disc           className="h-4 w-4" />,
    Bell:           <Bell           className="h-4 w-4" />,
    CalendarX:      <CalendarX      className="h-4 w-4" />,
    Music:          <Music          className="h-4 w-4" />,
    Lock:           <Lock           className="h-4 w-4" />,
    Users:          <Users          className="h-4 w-4" />,
    Clock:          <Clock          className="h-4 w-4" />,
    Disc2:          <Disc           className="h-4 w-4" />,
};

// ─────────────────────────────────────────────────────────────────────────────
// PRIMITIVES
// ─────────────────────────────────────────────────────────────────────────────

function CardShell({ children, className = "" }: { children: React.ReactNode; className?: string }) {
    return (
        <div className={`rounded-[2rem] bg-neutral-800 border border-white/5 overflow-hidden ${className}`}>
            {children}
        </div>
    );
}

function CardHeader({ title, icon, action }: { title: string; icon: React.ReactNode; action?: React.ReactNode }) {
    return (
        <div className="flex items-center gap-3 px-5 pt-5 pb-4 border-b border-white/10">
            <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-gradient-to-r from-[#547bb5] to-[#6e53b8] text-white">
                {icon}
            </div>
            <h3 className="flex-1 text-sm font-semibold text-white">{title}</h3>
            {action}
        </div>
    );
}

function DataRow({ icon, label, value, flag = false }: { icon?: React.ReactNode; label: string; value: string; flag?: boolean }) {
    return (
        <div className="flex items-start gap-3 border-b border-white/5 py-2.5 last:border-0">
            {icon && <div className="mt-0.5 shrink-0 text-white/40">{icon}</div>}
            <span className="shrink-0 text-sm text-white/50">{label}</span>
            <span className={`ml-auto text-right text-sm font-medium ${flag ? "text-amber-300" : "text-white"}`}>{value}</span>
        </div>
    );
}

function Divider() { return <div className="my-4 h-px bg-white/8" />; }

function FlipBtn({ onClick, label = "Details" }: { onClick?: () => void; label?: string }) {
    return (
        <button onClick={onClick}
            className="flex items-center gap-1.5 rounded-full border border-white/10 bg-white/5 px-3 py-1 text-[11px] font-semibold text-white/50 transition-colors hover:bg-white/10 hover:text-white">
            <RotateCcw className="h-3 w-3" /> {label}
        </button>
    );
}

function CustomTooltip({ active, payload, label }: { active?: boolean; payload?: { value: number }[]; label?: string }) {
    if (!active || !payload?.length) return null;
    return (
        <div className="rounded-xl border border-white/10 bg-neutral-900 px-3 py-2 text-sm shadow-xl">
            <div className="font-semibold text-white">{label}</div>
            <div className="text-white/50">${(payload[0].value / 1000).toFixed(0)}K committed</div>
        </div>
    );
}

// ─────────────────────────────────────────────────────────────────────────────
// FLIP CARD
// ─────────────────────────────────────────────────────────────────────────────

function FlipCard({ front, back }: { front: React.ReactNode; back: React.ReactNode }) {
    const [flipped, setFlipped] = useState(false);
    const frontRef = useRef<HTMLDivElement>(null);
    const [cardHeight, setCardHeight] = useState<number | undefined>(undefined);

    useEffect(() => {
        if (!frontRef.current) return;
        const ro = new ResizeObserver(() => {
            if (frontRef.current) setCardHeight(frontRef.current.offsetHeight);
        });
        ro.observe(frontRef.current);
        return () => ro.disconnect();
    }, []);

    return (
        <div style={{ perspective: "1400px", minHeight: cardHeight }}>
            <motion.div
                animate={{ rotateY: flipped ? 180 : 0 }}
                transition={{ duration: 0.52, ease: [0.22, 1, 0.36, 1] }}
                style={{ transformStyle: "preserve-3d", position: "relative", minHeight: "inherit" }}
            >
                <div ref={frontRef} style={{ backfaceVisibility: "hidden", WebkitBackfaceVisibility: "hidden" }}>
                    {React.cloneElement(front as React.ReactElement<{ onFlip?: () => void }>, { onFlip: () => setFlipped(true) })}
                </div>
                <div style={{ backfaceVisibility: "hidden", WebkitBackfaceVisibility: "hidden", transform: "rotateY(180deg)", position: "absolute", top: 0, left: 0, right: 0, minHeight: "100%" }}>
                    {React.cloneElement(back as React.ReactElement<{ onFlip?: () => void }>, { onFlip: () => setFlipped(false) })}
                </div>
            </motion.div>
        </div>
    );
}

// ─────────────────────────────────────────────────────────────────────────────
// ANIMATED PROGRESS BAR
// ─────────────────────────────────────────────────────────────────────────────

function AdvanceBar({ label, amount, total, isRecoup = false, delay = 0 }: {
    label: string; amount: number; total: number; isRecoup?: boolean; delay?: number;
}) {
    const ref = useRef<HTMLDivElement>(null);
    const inView = useInView(ref, { once: true });
    const [width, setWidth] = useState(0);
    const pct = Math.abs(amount) / total * 100;

    useEffect(() => {
        if (!inView) return;
        const ctrl = animate(0, pct, {
            duration: 0.9, delay, ease: [0.22, 1, 0.36, 1],
            onUpdate: v => setWidth(v),
        });
        return () => ctrl.stop();
    }, [inView, pct, delay]);

    return (
        <div ref={ref} className="space-y-1.5">
            <div className="flex items-center justify-between">
                <span className="text-xs text-white/50">{label}</span>
                <span className={`text-xs font-bold ${isRecoup ? "text-rose-300" : "text-white"}`}>
                    {isRecoup ? "−" : "+"}${(Math.abs(amount) / 1000).toFixed(0)}K
                </span>
            </div>
            <div className="h-2 w-full rounded-full bg-white/6 overflow-hidden">
                <motion.div
                    className={`h-full rounded-full ${isRecoup ? "bg-rose-400/60" : "bg-gradient-to-r from-[#547bb5] to-[#6e53b8]"}`}
                    style={{ width: `${width}%` }}
                />
            </div>
        </div>
    );
}

// ─────────────────────────────────────────────────────────────────────────────
// CARD 1 — MONEY  (FRONT: visual advance waterfall | BACK: payment schedule table)
// ─────────────────────────────────────────────────────────────────────────────

function MoneyFront({ onFlip, mounted, economics, editedTotal }: {
    onFlip?: () => void; mounted: boolean;
    economics: typeof DEMO.economics;
    editedTotal?: string;
}) {
    const displayTotal = editedTotal ? parseDollars(editedTotal) : economics.totalAdvance;
    const isEdited = !!editedTotal && parseDollars(editedTotal) !== economics.totalAdvance;

    return (
        <CardShell>
            <CardHeader title="Money You'll Receive" icon={<DollarSign className="h-4 w-4" />} action={<FlipBtn onClick={onFlip} label="Schedule" />} />
            <div className="px-5 py-4">

                {/* Hero number */}
                <div className="mb-5 rounded-2xl border border-white/5 bg-neutral-700 p-4">
                    <div className={`text-[10px] font-bold uppercase tracking-widest ${isEdited ? "text-amber-400" : "text-white/40"}`}>
                        {isEdited ? "⚡ Edited in doc" : "Total Advance (§3.1)"}
                    </div>
                    <div className={`mt-1 text-5xl font-black tracking-tight ${isEdited ? "text-amber-300" : "text-white"}`}>
                        {mounted ? (
                            <NumberFlow
                                value={displayTotal / 1000}
                                format={{ style: "currency", currency: "USD", maximumFractionDigits: 0 }}
                                suffix="K"
                            />
                        ) : `$${(displayTotal / 1000).toFixed(0)}K`}
                    </div>
                    <div className="mt-2 flex gap-3">
                        <span className="rounded-full bg-emerald-500/15 px-2.5 py-1 text-[10px] font-bold text-emerald-300">
                            Advance
                        </span>
                        <span className="rounded-full bg-rose-500/15 px-2.5 py-1 text-[10px] font-bold text-rose-300">
                            −${(economics.recoupAmount / 1000).toFixed(0)}K recoupable
                        </span>
                    </div>
                </div>

                {/* Animated advance bars */}
                <div className="mb-5 space-y-3">
                    {economics.advances.map((a, i) => (
                        <AdvanceBar
                            key={a.label}
                            label={a.label}
                            amount={a.amount}
                            total={economics.totalAdvance}
                            isRecoup={a.isRecoup}
                            delay={0.1 + i * 0.1}
                        />
                    ))}
                </div>

                {/* Royalty callout */}
                <div className="rounded-2xl border border-white/5 bg-neutral-700/60 px-4 py-3 flex items-center justify-between">
                    <div>
                        <div className="text-[10px] font-bold uppercase tracking-widest text-white/35">Backend Royalty (§5.1)</div>
                        <div className="mt-0.5 text-xs text-white/50">After full recoupment of advance + costs</div>
                    </div>
                    <div className="text-3xl font-black text-white">3%</div>
                </div>

                {/* Cumulative chart */}
                <div className="mt-4 h-20">
                    <ResponsiveContainer width="100%" height="100%">
                        <AreaChart data={economics.areaData} margin={{ top: 0, right: 0, left: 0, bottom: 0 }}>
                            <defs>
                                <linearGradient id="advgrad" x1="0" y1="0" x2="0" y2="1">
                                    <stop offset="5%"  stopColor="#547bb5" stopOpacity={0.5} />
                                    <stop offset="95%" stopColor="#6e53b8" stopOpacity={0}   />
                                </linearGradient>
                            </defs>
                            <XAxis dataKey="month" axisLine={false} tickLine={false} tick={{ fill: "rgba(255,255,255,0.25)", fontSize: 9 }} />
                            <Tooltip content={<CustomTooltip />} cursor={{ stroke: "rgba(255,255,255,0.06)", strokeWidth: 1 }} />
                            <Area type="monotone" dataKey="value" stroke="#547bb5" strokeWidth={2} fill="url(#advgrad)"
                                dot={false} isAnimationActive animationDuration={1400} animationEasing="ease-out" />
                        </AreaChart>
                    </ResponsiveContainer>
                </div>
                <div className="mt-1 text-center text-[10px] text-white/25">Cumulative advance — payment schedule</div>
            </div>
        </CardShell>
    );
}

function MoneyBack({ onFlip, economics }: { onFlip?: () => void; economics: typeof DEMO.economics }) {
    const nonRecoup = economics.advances.filter(a => !a.isRecoup);
    const guaranteed = nonRecoup.reduce((s, a) => s + a.amount, 0);

    return (
        <CardShell>
            <CardHeader title="Payment Schedule" icon={<DollarSign className="h-4 w-4" />} action={<FlipBtn onClick={onFlip} label="Back" />} />
            <div className="px-5 py-4 space-y-2.5">
                {economics.advances.map((item, i) => (
                    <motion.div key={item.label}
                        initial={{ opacity: 0, x: -8 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: i * 0.07 }}
                        className="flex items-center justify-between gap-3 rounded-2xl border border-white/5 bg-neutral-700 px-4 py-3"
                    >
                        <div>
                            <div className="text-sm font-medium text-white">{item.label}</div>
                        </div>
                        <div className="flex flex-col items-end gap-1">
                            <div className={`text-sm font-bold ${item.isRecoup ? "text-rose-300" : "text-white"}`}>
                                ${(item.amount / 1000).toFixed(0)}K
                            </div>
                            <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold ${item.isRecoup ? "bg-rose-500/15 text-rose-300" : "bg-emerald-500/15 text-emerald-300"}`}>
                                {item.isRecoup ? "Recoupable" : "Advance"}
                            </span>
                        </div>
                    </motion.div>
                ))}

                <Divider />
                <div className="space-y-1.5 text-sm">
                    <div className="flex justify-between text-white/50">
                        <span>Gross advance</span>
                        <span className="font-semibold text-white">${(economics.totalAdvance / 1000).toFixed(0)}K</span>
                    </div>
                    <div className="flex justify-between text-white/50">
                        <span>Recoup amount (§4.2)</span>
                        <span className="font-semibold text-rose-300">−${(economics.recoupAmount / 1000).toFixed(0)}K</span>
                    </div>
                    <div className="flex justify-between border-t border-white/10 pt-2">
                        <span className="font-semibold text-white/60">Royalties unlock after</span>
                        <span className="font-black text-white">${((economics.totalAdvance + economics.recoupAmount) / 1000).toFixed(0)}K recouped</span>
                    </div>
                </div>

                <div className="rounded-2xl border border-white/5 bg-neutral-700/50 px-4 py-3 flex items-center justify-between">
                    <div className="text-xs text-white/40">Backend royalty (§5.1)</div>
                    <div className="text-2xl font-black text-white">3% <span className="text-sm font-normal text-white/40">of gross receipts</span></div>
                </div>
            </div>
        </CardShell>
    );
}

// ─────────────────────────────────────────────────────────────────────────────
// CARD 2 — KEY DATES
// ─────────────────────────────────────────────────────────────────────────────

function KeyDates({ term }: { term: typeof DEMO.term }) {
    const ref = useRef<HTMLDivElement>(null);
    const inView = useInView(ref, { once: true });
    const [barH, setBarH] = useState(0);

    useEffect(() => {
        if (!inView) return;
        const ctrl = animate(0, 100, { duration: 1.1, ease: [0.22, 1, 0.36, 1], onUpdate: v => setBarH(v) });
        return () => ctrl.stop();
    }, [inView]);

    return (
        <CardShell>
            <CardHeader title="Key Dates & Term" icon={<CalendarDays className="h-4 w-4" />} />
            <div className="px-5 py-4">
                <div ref={ref} className="relative">
                    <div className="absolute bottom-0 left-[19px] top-0 w-[2px] rounded-full bg-white/6">
                        <div className="w-full rounded-full bg-gradient-to-b from-[#547bb5] to-[#6e53b8]"
                            style={{ height: `${barH}%`, transition: "height 0s" }} />
                    </div>
                    <div className="space-y-5 pl-10">
                        {term.keyDates.map((d, i) => (
                            <motion.div key={d.label}
                                initial={{ opacity: 0, x: -8 }} animate={inView ? { opacity: 1, x: 0 } : {}}
                                transition={{ delay: 0.15 + i * 0.1 }} className="relative"
                            >
                                <motion.div
                                    initial={{ scale: 0 }} animate={inView ? { scale: 1 } : {}}
                                    transition={{ type: "spring" as const, stiffness: 400, damping: 20, delay: 0.2 + i * 0.1 }}
                                    className="absolute -left-10 flex h-9 w-9 items-center justify-center rounded-full border-2 border-neutral-800 bg-gradient-to-br from-[#547bb5] to-[#6e53b8] text-white shadow-lg shadow-[#547bb5]/20"
                                >
                                    {ICON_MAP[d.icon]}
                                </motion.div>
                                <div className="text-[10px] font-bold uppercase tracking-widest text-white/35">{d.label}</div>
                                <div className="mt-0.5 text-[15px] font-bold text-white">{d.date}</div>
                                <div className="mt-0.5 text-xs text-white/40">{d.note}</div>
                            </motion.div>
                        ))}
                    </div>
                </div>
                <Divider />
                <DataRow label="Initial Term"       value={term.initialTerm} />
                <DataRow label="Options"            value={term.options} />
                <DataRow label="Post-Term Control"  value={term.postTermControl} />
                <DataRow label="Cure Period"        value={term.curePeriod} />
            </div>
        </CardShell>
    );
}

// ─────────────────────────────────────────────────────────────────────────────
// CARD 3 — RIGHTS (flip for ownership detail)
// ─────────────────────────────────────────────────────────────────────────────

function RightsFront({ onFlip, rights }: { onFlip?: () => void; rights: typeof DEMO.rights }) {
    return (
        <CardShell>
            <CardHeader title="Rights & Ownership" icon={<Megaphone className="h-4 w-4" />} action={<FlipBtn onClick={onFlip} label="Details" />} />
            <div className="px-5 py-4">
                <DataRow icon={<Globe className="h-4 w-4" />}  label="Territory"       value={rights.territory} />
                <DataRow icon={<Lock className="h-4 w-4" />}   label="Exclusivity"     value={rights.exclusivity} />
                <DataRow icon={<Timer className="h-4 w-4" />}  label="Post-Term"       value={rights.postTermControl} />
                <DataRow icon={<Eye className="h-4 w-4" />}    label="Artist Retains"  value={rights.artistRetains} />
                <Divider />
                <div className="mb-2 text-[10px] font-semibold uppercase tracking-widest text-white/35">Revenue Channels</div>
                <div className="flex flex-wrap gap-2">
                    {rights.channels.map(ch => (
                        <div key={ch} className="flex items-center gap-1.5 rounded-full border border-white/10 bg-white/5 px-3 py-1.5 text-xs font-medium text-white/60">
                            <Wifi className="h-3 w-3" />{ch}
                        </div>
                    ))}
                </div>
            </div>
        </CardShell>
    );
}

function RightsBack({ onFlip }: { onFlip?: () => void }) {
    const items = [
        { label: "Masters Ownership",    value: "Label — in perpetuity (WMFH)",   positive: false },
        { label: "Stems & Sessions",     value: "Label — per §1.2",               positive: false },
        { label: "Post-Term (3 yrs)",    value: "Label retains exploitation",      positive: false },
        { label: "After Post-Term",      value: "Rights revert to Artist",         positive: true  },
        { label: "Name & Likeness",      value: "Artist retains — §7.3",           positive: true  },
        { label: "Pre-existing IP",      value: "Artist retains — §7.3",           positive: true  },
    ];
    return (
        <CardShell>
            <CardHeader title="Ownership Breakdown" icon={<Lock className="h-4 w-4" />} action={<FlipBtn onClick={onFlip} label="Back" />} />
            <div className="px-5 py-4 space-y-2">
                {items.map((it, i) => (
                    <motion.div key={it.label}
                        initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.06 }}
                        className="flex items-center justify-between gap-3 rounded-2xl border border-white/5 bg-neutral-700 px-4 py-3"
                    >
                        <span className="text-sm text-white/60">{it.label}</span>
                        <span className={`text-right text-xs font-semibold ${it.positive ? "text-emerald-300" : "text-rose-300"}`}>{it.value}</span>
                    </motion.div>
                ))}
            </div>
        </CardShell>
    );
}

// ─────────────────────────────────────────────────────────────────────────────
// CARD 4 — OBLIGATIONS
// ─────────────────────────────────────────────────────────────────────────────

function ObligationsCard({ obligations }: { obligations: typeof DEMO.obligations }) {
    const [expanded, setExpanded] = useState<number | null>(null);
    return (
        <CardShell>
            <CardHeader title="Artist Obligations" icon={<FilePenLine className="h-4 w-4" />} />
            <div className="px-5 py-4 space-y-2">
                {obligations.map((ob, i) => (
                    <div key={ob.deliverable} className="overflow-hidden rounded-2xl border border-white/5 bg-neutral-700">
                        <button onClick={() => setExpanded(expanded === i ? null : i)}
                            className="flex w-full items-center gap-3 px-4 py-3 text-left transition-colors hover:bg-white/5">
                            <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-white/8 text-white/50">
                                {ICON_MAP[ob.icon]}
                            </div>
                            <span className="flex-1 text-sm font-medium text-white">{ob.deliverable}</span>
                            <motion.div animate={{ rotate: expanded === i ? 180 : 0 }} transition={{ duration: 0.2 }}>
                                <ChevronDown className="h-4 w-4 text-white/30" />
                            </motion.div>
                        </button>
                        <AnimatePresence>
                            {expanded === i && (
                                <motion.div
                                    initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }}
                                    exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.24, ease: [0.22, 1, 0.36, 1] }}
                                >
                                    <div className="border-t border-white/5 px-4 py-3">
                                        <div className="flex items-center gap-1.5 text-xs text-white/40">
                                            <Clock className="h-3 w-3" />
                                            <span className="font-semibold text-white/60">{ob.due}</span>
                                        </div>
                                        <div className="mt-1.5 text-xs text-white/40">{ob.note}</div>
                                    </div>
                                </motion.div>
                            )}
                        </AnimatePresence>
                    </div>
                ))}
            </div>
        </CardShell>
    );
}

// ─────────────────────────────────────────────────────────────────────────────
// CARD 5 — CLAUSES
// ─────────────────────────────────────────────────────────────────────────────

function ClausesCard({ clauses }: { clauses: typeof DEMO.clauses }) {
    const [open, setOpen] = useState<number | null>(null);
    return (
        <CardShell>
            <CardHeader title="Key Clauses" icon={<AlertTriangle className="h-4 w-4" />} />
            <div className="px-5 py-4 space-y-2">
                {clauses.map((clause, i) => (
                    <div key={clause.title} className="overflow-hidden rounded-2xl border border-white/5 bg-neutral-700">
                        <button onClick={() => setOpen(open === i ? null : i)}
                            className="flex w-full items-center gap-3 px-4 py-3 text-left hover:bg-white/5 transition-colors">
                            <div className="shrink-0">
                                {clause.flag ? <AlertTriangle className="h-4 w-4 text-amber-400" /> : <FileText className="h-4 w-4 text-white/40" />}
                            </div>
                            <span className="flex-1 text-sm font-medium text-white">{clause.title}</span>
                            <motion.div animate={{ rotate: open === i ? 180 : 0 }} transition={{ duration: 0.2 }}>
                                <ChevronDown className="h-4 w-4 text-white/30" />
                            </motion.div>
                        </button>
                        <AnimatePresence>
                            {open === i && (
                                <motion.div
                                    initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }}
                                    exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.25, ease: [0.22, 1, 0.36, 1] }}
                                >
                                    <div className="border-t border-white/5 px-4 py-4">
                                        <p className="text-sm leading-6 text-white/55">{clause.summary}</p>
                                        {clause.flag && (
                                            <div className="mt-3 flex items-start gap-2 rounded-xl border border-amber-400/15 bg-amber-500/8 px-3 py-2.5 text-xs text-amber-300">
                                                <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                                                {clause.flag}
                                            </div>
                                        )}
                                    </div>
                                </motion.div>
                            )}
                        </AnimatePresence>
                    </div>
                ))}
            </div>
        </CardShell>
    );
}

// ─────────────────────────────────────────────────────────────────────────────
// CARD 6 — REDLINE HISTORY
// ─────────────────────────────────────────────────────────────────────────────

function RedlineCard({ history }: { history: typeof DEMO.redlineHistory }) {
    return (
        <CardShell>
            <CardHeader title="Redline History" icon={<TrendingUp className="h-4 w-4" />} />
            <div className="px-5 pb-4">
                <div className="mb-3 flex items-center justify-between">
                    <span className="text-xs text-white/35">{history.length} changes · newest first</span>
                    <span className="rounded-full bg-[#547bb5]/20 px-2.5 py-0.5 text-[10px] font-bold text-[#9bbde0]">Live</span>
                </div>
                <div className="max-h-[340px] space-y-2 overflow-y-auto pr-1"
                    style={{ scrollbarWidth: "thin", scrollbarColor: "rgba(255,255,255,0.1) transparent" }}>
                    {history.map((entry, i) => {
                        const DI = entry.positive ? ArrowUpRight : ArrowDownRight;
                        return (
                            <motion.div key={i}
                                initial={{ opacity: 0, x: -6 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: i * 0.04 }}
                                className="rounded-2xl border border-white/5 bg-neutral-700 p-3.5"
                            >
                                <div className="mb-2 flex items-center gap-2">
                                    <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-[#547bb5] to-[#6e53b8] text-[9px] font-black text-white">
                                        {entry.initials}
                                    </div>
                                    <span className="text-xs font-semibold text-white/60">{entry.author}</span>
                                    <span className="ml-auto flex items-center gap-1 text-[10px] text-white/25">
                                        <Clock className="h-2.5 w-2.5" />{entry.timestamp}
                                    </span>
                                </div>
                                <div className="mb-1.5 text-xs font-bold uppercase tracking-widest text-white/35">{entry.clause}</div>
                                <div className="flex items-center justify-between gap-2">
                                    <div className="flex items-center gap-1.5">
                                        <span className="text-sm text-white/35 line-through">{entry.from}</span>
                                        <Minus className="h-3 w-3 text-white/20" />
                                        <span className="text-sm font-semibold text-white">{entry.to}</span>
                                    </div>
                                    <span className={`flex items-center gap-0.5 rounded-full px-2 py-0.5 text-[10px] font-bold ${entry.positive ? "bg-emerald-500/15 text-emerald-300" : "bg-rose-500/12 text-rose-300"}`}>
                                        <DI className="h-3 w-3" />{entry.delta}
                                    </span>
                                </div>
                            </motion.div>
                        );
                    })}
                </div>
            </div>
        </CardShell>
    );
}

// ─────────────────────────────────────────────────────────────────────────────
// CARD 7 — VALUE STRUCTURE (segmented stacked bar — from HTML prototype)
// ─────────────────────────────────────────────────────────────────────────────

function SegmentBar({ pct, delay, cls }: { pct: number; delay: number; cls: string }) {
    const ref = useRef<HTMLDivElement>(null);
    const inView = useInView(ref, { once: true });
    const [w, setW] = useState(0);
    useEffect(() => {
        if (!inView) return;
        const ctrl = animate(0, pct, { duration: 1.0, delay, ease: [0.22, 1, 0.36, 1], onUpdate: v => setW(v) });
        return () => ctrl.stop();
    }, [inView, pct, delay]);
    return <div ref={ref} className={`h-full rounded-full ${cls}`} style={{ width: `${w}%` }} />;
}

function ValueStructureCard({ economics }: { economics: typeof DEMO.economics }) {
    const total = economics.totalAdvance || 0;
    const recoup = economics.recoupAmount || 0;

    // Real engine data has an arbitrary number of advances (the DEMO shape's
    // fixed 4-instalment positional indexing crashed on real contracts). Drive
    // the segments straight off the actual non-recoup advances, with a small
    // gradient palette cycled across however many there are.
    const PALETTE = [
        { cls: "bg-gradient-to-r from-[#7c5cff] to-[#6157ff]", swatchCls: "bg-[#6157ff]" },
        { cls: "bg-gradient-to-r from-[#6b75ff] to-[#8094ff]", swatchCls: "bg-[#8094ff]" },
        { cls: "bg-gradient-to-r from-[#a7b8ff] to-[#c2ccff]", swatchCls: "bg-[#c2ccff]" },
        { cls: "bg-gradient-to-r from-[#547bb5] to-[#6e53b8]", swatchCls: "bg-[#547bb5]" },
    ];
    const segments = (economics.advances ?? [])
        .filter(a => a && !a.isRecoup && a.amount > 0)
        .map((a, i) => ({
            label: a.label || `Instalment ${i + 1}`,
            amount: a.amount,
            pct: total > 0 ? (a.amount / total) * 100 : 0,
            ...PALETTE[i % PALETTE.length],
        }));

    const totalLabel = total >= 1000 ? `$${(total / 1000).toFixed(0)}K` : `$${total.toLocaleString()}`;

    return (
        <CardShell>
            <CardHeader title="Value Structure" icon={<DollarSign className="h-4 w-4" />} />
            <div className="px-5 py-4">
                <div className="mb-2 text-[10px] font-bold uppercase tracking-widest text-white/35">Total Advance (§3.1)</div>
                <div className="mb-4 text-4xl font-black tracking-tight text-white">{totalLabel}</div>

                {/* Segmented stacked bar */}
                {segments.length > 0 && (
                    <div className="h-5 w-full overflow-hidden rounded-full bg-white/6 border border-white/5 flex mb-5">
                        {segments.map((s, i) => (
                            <SegmentBar key={s.label} pct={s.pct} delay={i * 0.15} cls={s.cls} />
                        ))}
                    </div>
                )}

                {/* Legend */}
                <div className="space-y-3">
                    {segments.map(s => (
                        <div key={s.label} className="flex items-center justify-between gap-3">
                            <div className="flex items-center gap-2.5">
                                <div className={`h-2.5 w-2.5 rounded-sm flex-shrink-0 ${s.swatchCls}`} />
                                <span className="text-sm text-white/60">{s.label}</span>
                            </div>
                            <span className="text-sm font-bold text-white">${(s.amount / 1000).toFixed(0)}K</span>
                        </div>
                    ))}
                    {segments.length === 0 && (
                        <div className="text-sm text-white/40">No instalment breakdown extracted for this contract.</div>
                    )}
                    {recoup > 0 && (
                        <div className="mt-2 flex items-center justify-between gap-3 rounded-2xl border border-rose-500/15 bg-rose-500/8 px-3 py-2">
                            <div className="flex items-center gap-2.5">
                                <div className="h-2.5 w-2.5 rounded-sm bg-rose-400 flex-shrink-0" />
                                <span className="text-sm text-rose-300/80">Recoupable Costs</span>
                            </div>
                            <span className="text-sm font-bold text-rose-300">−${(recoup / 1000).toFixed(0)}K</span>
                        </div>
                    )}
                </div>
            </div>
        </CardShell>
    );
}

// ─────────────────────────────────────────────────────────────────────────────
// CARD 8 — HORIZONTAL DEAL TIMELINE (from HTML prototype)
// ─────────────────────────────────────────────────────────────────────────────

function HorizontalTimeline({ term }: { term: typeof DEMO.term }) {
    const ref = useRef<HTMLDivElement>(null);
    const inView = useInView(ref, { once: true });
    const [lineW, setLineW] = useState(0);
    useEffect(() => {
        if (!inView) return;
        const ctrl = animate(0, 100, { duration: 1.2, ease: [0.22, 1, 0.36, 1], onUpdate: v => setLineW(v) });
        return () => ctrl.stop();
    }, [inView]);

    return (
        <CardShell>
            <CardHeader title="Deal Timeline" icon={<CalendarDays className="h-4 w-4" />} />
            <div className="px-5 py-6" ref={ref}>
                <div className="text-[10px] font-bold uppercase tracking-widest text-white/35 mb-6">Term Architecture — key timing milestones</div>

                {/* Horizontal timeline */}
                <div className="relative">
                    {/* Track */}
                    <div className="absolute top-[10px] left-0 right-0 h-[3px] rounded-full bg-white/8" />
                    {/* Animated fill */}
                    <div
                        className="absolute top-[10px] left-0 h-[3px] rounded-full bg-gradient-to-r from-[#7c5cff] to-[#547bb5]"
                        style={{ width: `${lineW}%`, transition: "width 0s" }}
                    />
                    {/* Dots + labels */}
                    <div className="flex justify-between relative">
                        {term.keyDates.map((d, i) => (
                            <motion.div
                                key={d.label}
                                initial={{ opacity: 0, y: 6 }}
                                animate={inView ? { opacity: 1, y: 0 } : {}}
                                transition={{ delay: 0.2 + i * 0.15 }}
                                className="flex flex-col items-center gap-0"
                                style={{ width: `${100 / term.keyDates.length}%` }}
                            >
                                <motion.div
                                    initial={{ scale: 0 }}
                                    animate={inView ? { scale: 1 } : {}}
                                    transition={{ type: "spring" as const, stiffness: 400, damping: 18, delay: 0.3 + i * 0.15 }}
                                    className="h-5 w-5 rounded-full bg-gradient-to-br from-[#7c5cff] to-[#5b8cff] border-2 border-neutral-800 shadow-lg shadow-[#7c5cff]/30 mb-3 z-10 relative"
                                />
                                <div className="text-center">
                                    <div className="text-[11px] font-bold text-white leading-tight">{d.label}</div>
                                    <div className="text-[10px] text-white/40 mt-0.5">{d.date}</div>
                                </div>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </div>
        </CardShell>
    );
}

// ─────────────────────────────────────────────────────────────────────────────
// CARD 9 — RISK EXPOSURE MAP (from HTML prototype)
// ─────────────────────────────────────────────────────────────────────────────

const RISK_ITEMS = [
    { label: "Master Ownership",       tier: "High Exposure",   cls: "bg-rose-500/15    text-rose-300",    note: "Label owns in perpetuity — no reversion (§7.1)" },
    { label: "Recoupment Threshold",   tier: "High Exposure",   cls: "bg-rose-500/15    text-rose-300",    note: "$515K must be recouped before any royalties (§4.4)" },
    { label: "Backend Royalty Rate",   tier: "Below Market",    cls: "bg-amber-500/15   text-amber-300",   note: "3% vs industry standard 15–25% (§5.1)" },
    { label: "Unilateral Options",     tier: "Medium",          cls: "bg-amber-500/15   text-amber-300",   note: "2 × 1-year at Label's sole discretion (§6.2)" },
    { label: "Liability Cap",          tier: "Favorable",       cls: "bg-emerald-500/15 text-emerald-300", note: "Capped at total amounts paid (§mutual)" },
    { label: "Cure Period",            tier: "Acceptable",      cls: "bg-emerald-500/15 text-emerald-300", note: "30 days to remedy breach before termination (§10.1)" },
];

function RiskExposureCard() {
    return (
        <CardShell>
            <CardHeader title="Risk / Exposure Map" icon={<AlertTriangle className="h-4 w-4" />} />
            <div className="px-5 py-4">
                <div className="text-[10px] font-bold uppercase tracking-widest text-white/35 mb-4">
                    Fast-glance contractual pressure points
                </div>
                <div className="space-y-2">
                    {RISK_ITEMS.map((item, i) => (
                        <motion.div
                            key={item.label}
                            initial={{ opacity: 0, x: -6 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ delay: i * 0.06 }}
                            className="flex items-start justify-between gap-3 rounded-2xl border border-white/5 bg-neutral-700 px-4 py-3"
                        >
                            <div>
                                <div className="text-sm font-semibold text-white">{item.label}</div>
                                <div className="mt-0.5 text-xs text-white/35">{item.note}</div>
                            </div>
                            <span className={`shrink-0 rounded-full px-2.5 py-1 text-[10px] font-bold ${item.cls}`}>{item.tier}</span>
                        </motion.div>
                    ))}
                </div>
            </div>
        </CardShell>
    );
}

// ─────────────────────────────────────────────────────────────────────────────
// CARD 10 — DEAL SCORE
// ─────────────────────────────────────────────────────────────────────────────

function ScoreRing({ score, size = 130 }: { score: number; size?: number }) {
    const ref = useRef<HTMLDivElement>(null);
    const inView = useInView(ref, { once: true });
    const [animScore, setAnimScore] = useState(0);

    const radius = (size - 20) / 2;
    const circ = 2 * Math.PI * radius;
    const cx = size / 2;
    const cy = size / 2;
    // Arc sweeps 270° (from 135° to 405°), starting bottom-left
    const arcPct = (animScore / 100) * 0.75;
    const dashArr = circ;
    const dashOffset = circ * (1 - arcPct);

    const color = score >= 75 ? "#34d399" : score >= 50 ? "#fbbf24" : "#f87171";
    const trackColor = "rgba(255,255,255,0.06)";

    useEffect(() => {
        if (!inView) return;
        const ctrl = animate(0, score, {
            duration: 1.4, ease: [0.22, 1, 0.36, 1],
            onUpdate: v => setAnimScore(Math.round(v)),
        });
        return () => ctrl.stop();
    }, [inView, score]);

    return (
        <div ref={ref} className="relative flex items-center justify-center" style={{ width: size, height: size }}>
            <svg width={size} height={size} style={{ transform: "rotate(135deg)" }}>
                {/* Track */}
                <circle cx={cx} cy={cy} r={radius}
                    fill="none" stroke={trackColor} strokeWidth={10}
                    strokeDasharray={`${circ * 0.75} ${circ * 0.25}`}
                    strokeLinecap="round"
                />
                {/* Fill */}
                <circle cx={cx} cy={cy} r={radius}
                    fill="none" stroke={color} strokeWidth={10}
                    strokeLinecap="round"
                    strokeDasharray={`${dashArr * 0.75} ${dashArr * 0.25}`}
                    strokeDashoffset={dashOffset}
                    style={{ filter: `drop-shadow(0 0 8px ${color}60)`, transition: "stroke-dashoffset 0s" }}
                />
            </svg>
            {/* Center label */}
            <div className="absolute flex flex-col items-center">
                <span className="text-4xl font-black text-white leading-none">{animScore}</span>
                <span className="text-[10px] font-bold text-white/35 uppercase tracking-widest">/100</span>
            </div>
        </div>
    );
}

function DealScoreFront({ onFlip, score }: { onFlip?: () => void; score: typeof DEMO.dealScore }) {
    const ref = useRef<HTMLDivElement>(null);
    const inView = useInView(ref, { once: true });
    const color = score.overall >= 75 ? "text-emerald-300" : score.overall >= 50 ? "text-amber-300" : "text-rose-300";
    const badgeCls = score.overall >= 75
        ? "bg-emerald-500/15 text-emerald-300"
        : score.overall >= 50
        ? "bg-amber-500/15 text-amber-300"
        : "bg-rose-500/15 text-rose-300";

    return (
        <CardShell>
            <CardHeader title="Deal Score" icon={<ShieldCheck className="h-4 w-4" />} action={<FlipBtn onClick={onFlip} label="Breakdown" />} />
            <div className="px-5 py-4">
                {/* Score ring + label row */}
                <div className="flex items-center gap-5 mb-5">
                    <div className="relative flex-shrink-0">
                        <ScoreRing score={score.overall} size={120} />
                    </div>
                    <div>
                        <div className={`text-xl font-black ${color}`}>{score.label}</div>
                        <div className="mt-1 text-xs text-white/40 leading-relaxed">{score.sublabel}</div>
                        <div className={`mt-2 inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[10px] font-bold ${badgeCls}`}>
                            <ShieldCheck className="h-3 w-3" />
                            {score.overall}/100 · Equal-IQ Score
                        </div>
                    </div>
                </div>

                {/* Factor bars */}
                <div ref={ref} className="space-y-2.5">
                    {score.factors.map((f, i) => (
                        <FactorBar key={f.name} factor={f} delay={0.1 + i * 0.08} inView={inView} />
                    ))}
                </div>
            </div>
        </CardShell>
    );
}

function FactorBar({ factor, delay, inView }: {
    factor: typeof DEMO.dealScore.factors[number];
    delay: number;
    inView: boolean;
}) {
    const [w, setW] = useState(0);
    const color = factor.score >= 70 ? "bg-gradient-to-r from-emerald-500 to-emerald-400"
        : factor.score >= 40 ? "bg-gradient-to-r from-amber-500 to-amber-400"
        : "bg-gradient-to-r from-rose-500 to-rose-400";
    const textColor = factor.score >= 70 ? "text-emerald-300" : factor.score >= 40 ? "text-amber-300" : "text-rose-300";

    useEffect(() => {
        if (!inView) return;
        const ctrl = animate(0, factor.score, {
            duration: 0.9, delay, ease: [0.22, 1, 0.36, 1],
            onUpdate: v => setW(v),
        });
        return () => ctrl.stop();
    }, [inView, factor.score, delay]);

    return (
        <div className="space-y-1">
            <div className="flex items-center justify-between">
                <span className="text-xs text-white/55">{factor.name}</span>
                <span className={`text-xs font-bold ${textColor}`}>{factor.score}</span>
            </div>
            <div className="h-1.5 w-full rounded-full bg-white/6 overflow-hidden">
                <div className={`h-full rounded-full ${color}`} style={{ width: `${w}%`, transition: "width 0s" }} />
            </div>
        </div>
    );
}

function DealScoreBack({ onFlip, score }: { onFlip?: () => void; score: typeof DEMO.dealScore }) {
    return (
        <CardShell>
            <CardHeader title="Score Breakdown" icon={<ShieldCheck className="h-4 w-4" />} action={<FlipBtn onClick={onFlip} label="Back" />} />
            <div className="px-5 py-4 space-y-2">
                <div className="text-[10px] font-bold uppercase tracking-widest text-white/35 mb-3">
                    Weighted factor analysis — Equal-IQ methodology
                </div>
                {score.factors.map((f, i) => {
                    const contribution = Math.round((f.score * f.weight) / 100);
                    const textColor = f.score >= 70 ? "text-emerald-300" : f.score >= 40 ? "text-amber-300" : "text-rose-300";
                    const badgeCls = f.score >= 70
                        ? "bg-emerald-500/15 text-emerald-300"
                        : f.score >= 40
                        ? "bg-amber-500/15 text-amber-300"
                        : "bg-rose-500/15 text-rose-300";
                    return (
                        <motion.div key={f.name}
                            initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.06 }}
                            className="rounded-2xl border border-white/5 bg-neutral-700 px-4 py-3"
                        >
                            <div className="flex items-center justify-between mb-1">
                                <span className="text-sm font-semibold text-white">{f.name}</span>
                                <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold ${badgeCls}`}>
                                    {f.score}/100
                                </span>
                            </div>
                            <div className="flex items-center justify-between">
                                <span className="text-xs text-white/40">{f.note}</span>
                                <span className="text-xs text-white/30 ml-3 shrink-0">
                                    ×{f.weight}% = <span className={`font-bold ${textColor}`}>{contribution}pts</span>
                                </span>
                            </div>
                        </motion.div>
                    );
                })}
                {/* Total */}
                <div className="mt-3 flex items-center justify-between rounded-2xl border border-white/10 bg-neutral-600 px-4 py-3">
                    <span className="text-sm font-semibold text-white/60">Weighted Total</span>
                    <span className="text-2xl font-black text-amber-300">{score.overall}<span className="text-sm font-normal text-white/35">/100</span></span>
                </div>
            </div>
        </CardShell>
    );
}

// ─────────────────────────────────────────────────────────────────────────────
// Path A — "Not extracted yet" placeholder for sections the engine can't yet
// populate. Mirrors the muted-card aesthetic of CardShell so the section keeps
// its slot in the grid + page rhythm rather than disappearing. The B-track
// (clauses, redline, risk derivation, rights extraction) replaces these
// placeholders one section at a time as engine capability lands.
// ─────────────────────────────────────────────────────────────────────────────

function NotExtractedYet({ section, icon, hint }: { section: string; icon: React.ReactNode; hint?: string }) {
    return (
        <CardShell className="opacity-60">
            <CardHeader title={section} icon={icon} />
            <div className="px-5 py-8 text-center">
                <div className="mb-2 text-sm font-medium text-white/50">Not extracted yet</div>
                <div className="mx-auto max-w-[280px] text-xs text-white/35 leading-relaxed">
                    {hint ?? "The deal-processing engine doesn't surface this field yet. Coming in a follow-up."}
                </div>
            </div>
        </CardShell>
    );
}

// ─────────────────────────────────────────────────────────────────────────────
// Path A extractors — read real engine output into the shapes each card
// component already expects. Each returns null when the engine didn't produce
// the field (or didn't produce enough fields to render meaningfully). null →
// section renders <NotExtractedYet>. Non-null → section renders its card.
//
// We construct shapes matching `typeof DEMO.X` so the existing card component
// signatures keep working without changes — DEMO stays in the file as the
// type source-of-truth, but its values are never used at runtime when a
// real `contract` is provided. (DEMO still drives the standalone-embed
// path where SportsContractCardsDemo is rendered without any contract prop.)
// ─────────────────────────────────────────────────────────────────────────────

function extractEconomics(contract: ContractData | undefined): typeof DEMO.economics | null {
    if (!contract) return null;
    const facts = contract.dealFacts;
    const moneyRaw = contract.eqCardsRaw?.cards?.money;
    const moneyLegacy = contract.eqData?.cards?.moneyYouReceive?.cardUniqueData?.MONEY_RECEIVED;

    const totalAdvance = facts?.totalAdvance?.amountMajor
        ?? moneyRaw?.primaryAmount
        ?? parseDollars(moneyLegacy?.majorNumber);
    if (!totalAdvance) return null;

    // Per-instalment amounts come from the engine's CardFact[] (label+value
    // strings like "Signing bonus" / "$145,000") OR from the legacy
    // paidAfterList strings. We parse dollars out of the value when present;
    // when we can't, the bar renders with amount=0 (still visible as a label
    // in the advance list, just no proportion).
    const advanceFacts = moneyRaw?.facts ?? [];
    const advances = advanceFacts.length > 0
        ? advanceFacts.map(f => ({
            label: f.label ?? "",
            amount: parseDollars(f.value) ?? 0,
            isRecoup: /recoup|recover/i.test(f.label ?? "") || /recoup|recover/i.test(f.value ?? ""),
        }))
        : (moneyLegacy?.paidAfterList ?? []).map(item => ({
            label: item,
            amount: parseDollars(item) ?? 0,
            isRecoup: /recoup|recover/i.test(item),
        }));

    const backendRoyaltyPct = facts?.backendRoyaltyPercent ?? 0;
    const recoupAmount = advances.find(a => a.isRecoup)?.amount ?? 0;

    // Cumulative cash-flow timeline. Engine doesn't emit this directly today
    // (B3 will extract per-instalment paidOn dates from contract text). For
    // now derive a flat 4-quarter ramp from `effectiveDate` + advance amounts
    // so the chart renders something honest about cumulative-to-date even
    // without per-instalment dates.
    const areaData = deriveAreaData(advances, facts?.effectiveDate);

    return { totalAdvance, advances, recoupAmount, backendRoyaltyPct, areaData };
}

function deriveAreaData(advances: { label: string; amount: number; isRecoup: boolean }[], effectiveDate?: string | null): typeof DEMO.economics.areaData {
    if (!advances.length) return [];
    let cumulative = 0;
    const start = effectiveDate ? new Date(effectiveDate) : new Date();
    return advances.map((a, i) => {
        cumulative += a.amount;
        const d = new Date(start);
        d.setMonth(d.getMonth() + i * 3);
        const month = d.toLocaleString('en-US', { month: 'short' }) + " '" + String(d.getFullYear()).slice(2);
        return { month, value: cumulative };
    });
}

function extractTerm(contract: ContractData | undefined): typeof DEMO.term | null {
    if (!contract) return null;
    const facts = contract.dealFacts;
    const t = facts?.term;
    const durLegacy = contract.eqData?.cards?.howLongThisDealLasts?.cardUniqueData?.DURATION?.durationText;

    const initialTerm = t?.description
        || (t?.years ? `${t.years} year${t.years === 1 ? '' : 's'}` : null)
        || durLegacy
        || null;
    if (!initialTerm) return null;

    // Options / postTermControl / noticePeriod / curePeriod aren't structured
    // engine fields today — leave as engine-blank placeholders rather than
    // copying DEMO values. The card component renders them as plain text.
    return {
        initialTerm,
        options: "Not extracted yet",
        postTermControl: "Not extracted yet",
        noticePeriod: "Not extracted yet",
        curePeriod: "Not extracted yet",
        keyDates: extractKeyDates(facts?.effectiveDate, t?.years),
    };
}

function extractKeyDates(effectiveDate: string | null | undefined, termYears: number | null | undefined): typeof DEMO.term.keyDates {
    if (!effectiveDate) return [];
    const start = new Date(effectiveDate);
    const dates: typeof DEMO.term.keyDates = [
        { label: "Effective Date", date: start.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }), note: "Agreement takes effect", icon: "CalendarCheck2" },
    ];
    if (termYears) {
        const expiry = new Date(start);
        expiry.setFullYear(expiry.getFullYear() + termYears);
        dates.push({ label: "Term Expiry", date: expiry.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }), note: `End of ${termYears}-year initial term`, icon: "CalendarX" });
    }
    return dates;
}

function extractObligations(contract: ContractData | undefined): typeof DEMO.obligations | null {
    if (!contract) return null;
    const deliverables = contract.dealFacts?.deliverables ?? [];
    const respLegacy = contract.eqData?.cards?.whatYoureResponsibleFor?.cardUniqueData?.RESPONSIBILITIES?.responsibilities;
    const respRawItems = contract.eqCardsRaw?.cards?.responsibilities?.items ?? [];

    const items = deliverables.length > 0 ? deliverables
        : respRawItems.length > 0 ? respRawItems
        : (respLegacy ?? []).map(r => r.description ?? r.title ?? "");

    if (!items.length) return null;
    return items.filter(s => s).map(text => ({
        deliverable: text,
        due: "",
        note: "",
        // Lightweight icon hint from text content — keyword match. Falls back to "Music".
        icon: /master|recording|stem/i.test(text) ? "Disc"
            : /promot|press|interview|appear/i.test(text) ? "Users"
            : /timely|schedule|deliver/i.test(text) ? "Clock"
            : /exclusive|consent|approval/i.test(text) ? "Lock"
            : "Music",
    }));
}

function extractDealScore(contract: ContractData | undefined): typeof DEMO.dealScore | null {
    const score = contract?.dealScore;
    if (!score?.overall && score?.overall !== 0) return null;

    // Six factor weights from db-model/src/lib/dealScore.ts:26-32. Keep them
    // synced manually here — small array, low churn risk. `positive` is a
    // visual hint derived from score (>= 50 = positive).
    const WEIGHTS = { advance: 25, royalty: 20, ownership: 20, term: 15, postTermControl: 10, curePeriod: 10 };
    const FACTOR_NAMES: Record<keyof typeof WEIGHTS, string> = {
        advance: "Advance Size",
        royalty: "Royalty Rate",
        ownership: "Master Ownership",
        term: "Term & Flexibility",
        postTermControl: "Post-Term Control",
        curePeriod: "Cure Period",
    };

    const breakdown = score.breakdown ?? {};
    const factors: typeof DEMO.dealScore.factors = (Object.keys(WEIGHTS) as (keyof typeof WEIGHTS)[]).map(key => {
        const f = breakdown[key];
        const factorScore = (f?.score ?? null);
        return {
            name: FACTOR_NAMES[key],
            score: typeof factorScore === 'number' ? factorScore : 0,
            weight: WEIGHTS[key],
            note: f?.reason ?? (f?.available === false ? "Not specified in contract" : ""),
            positive: typeof factorScore === 'number' && factorScore >= 50,
        };
    });

    return {
        overall: score.overall,
        label: score.label ?? "Fair Deal",
        sublabel: factors.filter(f => !f.positive).length > 0
            ? `${factors.filter(f => !f.positive).length} artist-unfavorable factors flagged`
            : "Balanced terms",
        factors,
    };
}

function extractRights(contract: ContractData | undefined): typeof DEMO.rights | null {
    const ownership = contract?.dealFacts?.masterOwnership;
    if (!ownership?.description && !ownership?.kind) return null;

    // Rights extraction is intentionally thin until B5 — we only have
    // masterOwnership from dealFacts today. Return a minimal Rights shape
    // rather than full DEMO. Other fields are honest "not extracted".
    return {
        territory: "Not extracted yet",
        exclusivity: "Not extracted yet",
        masterOwnership: ownership.description ?? ownership.kind ?? "",
        postTermControl: "Not extracted yet",
        artistRetains: "Not extracted yet",
        channels: [],
    };
}

// ─────────────────────────────────────────────────────────────────────────────

interface SportsContractCardsDemoProps {
    embedded?: boolean;
    contract?: ContractData;
    taggedContractText?: string;
    /**
     * When true (default), a missing `contract` falls back to the hand-authored
     * DEMO dataset — the standalone-preview path (RamVizPage, /demo routes) that
     * has no deal context but still wants to render the cards. On REAL routes the
     * caller passes `false` so a real deal with no extracted contract renders the
     * section <NotExtractedYet> empties instead of demo fiction (Midnight Records
     * × Alexandra Rivers). Default preserves every existing standalone usage.
     */
    demoFallback?: boolean;
}

export default function SportsContractCardsDemo({
    embedded = false,
    contract,
    taggedContractText,
    demoFallback = true,
}: SportsContractCardsDemoProps) {
    const [mounted, setMounted] = useState(false);
    useEffect(() => { setMounted(true); }, []);

    // Parse live variable values from tagged document text
    const liveVars = useMemo(() =>
        taggedContractText ? parseTaggedVars(taggedContractText) : {} as Record<string, string>,
        [taggedContractText]
    );

    // Path A — extract per-section data from the engine output. When `contract`
    // is undefined we fall back to DEMO across the board ONLY if `demoFallback`
    // is on (standalone-preview path: RamVizPage, /demo routes). On a REAL route
    // the caller passes demoFallback={false}, so a real deal with no extracted
    // contract renders the section <NotExtractedYet> empties instead of demo
    // fiction. When `contract` IS provided, each extractor returns either a
    // real-data shape or null; null sections render <NotExtractedYet>. No more
    // lying about extracted values.
    const isStandaloneDemo = !contract && demoFallback;
    const economics = useMemo(() => isStandaloneDemo ? DEMO.economics : extractEconomics(contract), [contract, isStandaloneDemo]);
    const term = useMemo(() => isStandaloneDemo ? DEMO.term : extractTerm(contract), [contract, isStandaloneDemo]);
    const obligations = useMemo(() => isStandaloneDemo ? DEMO.obligations : extractObligations(contract), [contract, isStandaloneDemo]);
    const dealScore = useMemo(() => isStandaloneDemo ? DEMO.dealScore : extractDealScore(contract), [contract, isStandaloneDemo]);
    const rights = useMemo(() => isStandaloneDemo ? DEMO.rights : extractRights(contract), [contract, isStandaloneDemo]);
    // Sections the engine doesn't produce yet (B-track will fill in):
    const clauses = isStandaloneDemo ? DEMO.clauses : null;          // B1 — new clauseSummaries LLM stage
    const redlineHistory = isStandaloneDemo ? DEMO.redlineHistory : null;  // B2 — version-diff verb
    const showRiskExposure = isStandaloneDemo;                       // B4 — derive from dealScore + low-confidence IQ
    // The header status pill — "Demo" only on the standalone-preview path, "Live"
    // otherwise (so a real route with an empty contract shows "Live", not "Demo").
    const sourceLabel = isStandaloneDemo ? "Demo" : "Live";

    return (
        <div className={embedded ? "p-3 text-white" : "min-h-screen bg-[#111113] p-4 text-white md:p-6"}>
            <div className="w-full">
                {!embedded && (
                    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
                        className="mb-4 flex items-center justify-between">
                        <div>
                            <div className="text-[10px] font-semibold uppercase tracking-widest text-white/35">Midnight Records × Alexandra Rivers</div>
                            <h2 className="mt-0.5 text-lg font-bold text-white">Recording Deal Cards</h2>
                        </div>
                        <div className="flex items-center gap-2">
                            <span className="flex h-2 w-2 rounded-full bg-emerald-400 shadow-[0_0_6px_rgba(52,211,153,0.8)]" />
                            <span className="text-xs text-white/40">{sourceLabel}</span>
                        </div>
                    </motion.div>
                )}

                <motion.div variants={stagger} initial="hidden" animate="show" className={embedded ? "space-y-4" : "grid grid-cols-2 gap-4 items-start"}>
                    {/* Money */}
                    <motion.div variants={fadeUp}>
                        {economics
                            ? <FlipCard
                                front={<MoneyFront mounted={mounted} economics={economics} editedTotal={undefined} />}
                                back={<MoneyBack economics={economics} />}
                            />
                            : <NotExtractedYet section="Money You'll Receive" icon={<DollarSign className="h-4 w-4" />} hint="The engine didn't extract a total advance from this contract." />
                        }
                    </motion.div>

                    {/* Key Dates */}
                    <motion.div variants={fadeUp}>
                        {term
                            ? <KeyDates term={term} />
                            : <NotExtractedYet section="Key Dates & Term" icon={<CalendarDays className="h-4 w-4" />} hint="No structured term extracted from this contract." />
                        }
                    </motion.div>

                    {/* Rights & Ownership */}
                    <motion.div variants={fadeUp}>
                        {rights
                            ? <FlipCard front={<RightsFront rights={rights} />} back={<RightsBack />} />
                            : <NotExtractedYet section="Rights & Ownership" icon={<ShieldCheck className="h-4 w-4" />} hint="Master ownership not extracted yet — see B5." />
                        }
                    </motion.div>

                    {/* Artist Obligations */}
                    <motion.div variants={fadeUp}>
                        {obligations
                            ? <ObligationsCard obligations={obligations} />
                            : <NotExtractedYet section="Artist Obligations" icon={<FilePenLine className="h-4 w-4" />} hint="No deliverables extracted from this contract." />
                        }
                    </motion.div>

                    {/* Key Clauses (B1 — clauseSummaries LLM stage pending) */}
                    <motion.div variants={fadeUp}>
                        {clauses
                            ? <ClausesCard clauses={clauses} />
                            : <NotExtractedYet section="Key Clauses" icon={<FileText className="h-4 w-4" />} hint="Clause summarization stage pending — see Path B1." />
                        }
                    </motion.div>

                    {/* Value Structure (derived from economics) */}
                    <motion.div variants={fadeUp}>
                        {economics
                            ? <ValueStructureCard economics={economics} />
                            : <NotExtractedYet section="Value Structure" icon={<TrendingUp className="h-4 w-4" />} hint="No advance/royalty data — chart needs economics." />
                        }
                    </motion.div>

                    {/* Deal Timeline (same term data) */}
                    <motion.div variants={fadeUp}>
                        {term
                            ? <HorizontalTimeline term={term} />
                            : <NotExtractedYet section="Deal Timeline" icon={<Clock className="h-4 w-4" />} hint="No term + key dates extracted." />
                        }
                    </motion.div>

                    {/* Risk / Exposure Map (B4 — derivation from dealScore + IQ pending) */}
                    <motion.div variants={fadeUp}>
                        {showRiskExposure
                            ? <RiskExposureCard />
                            : <NotExtractedYet section="Risk / Exposure Map" icon={<AlertTriangle className="h-4 w-4" />} hint="Risk derivation pending — see Path B4." />
                        }
                    </motion.div>

                    {/* Redline History (B2 — version-diff verb pending) */}
                    <motion.div variants={fadeUp}>
                        {redlineHistory
                            ? <RedlineCard history={redlineHistory} />
                            : <NotExtractedYet section="Redline History" icon={<RotateCcw className="h-4 w-4" />} hint="Version-diff verb pending — see Path B2. Shows here once a deal has ≥2 versions." />
                        }
                    </motion.div>

                    {/* Deal Score */}
                    <motion.div variants={fadeUp} className={embedded ? "" : "col-span-2"}>
                        {dealScore
                            ? <FlipCard
                                front={<DealScoreFront score={dealScore} />}
                                back={<DealScoreBack score={dealScore} />}
                            />
                            : <NotExtractedYet section="Deal Score" icon={<ShieldCheck className="h-4 w-4" />} hint="The engine didn't produce a fairness score for this contract." />
                        }
                    </motion.div>
                </motion.div>
                <div className="h-8" />
            </div>
        </div>
    );
}